import {
  CHAT_DRAFT_STORAGE_KEY,
  parseChatDraft,
  type ChatDraft,
} from "./draft";
import { CHAT_ALLOWED_LOCAL_PAGE_ORIGINS, CHAT_ALLOWED_PAGE_HOSTS } from "./config";

(() => {
  const FRAME_ID = "studio-tche-chat-widget";
  const SOURCE = "studio-tche-chat-widget";
  const ALLOWED_ORIGINS = new Set([
    ...CHAT_ALLOWED_PAGE_HOSTS.map((host) => "https://" + host),
    ...CHAT_ALLOWED_LOCAL_PAGE_ORIGINS,
  ]);
  const isDevelopmentHost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (document.getElementById(FRAME_ID)) return;
  if (!ALLOWED_ORIGINS.has(window.location.origin) && !isDevelopmentHost) {
    console.warn("[Studio Tchê] O chat não está autorizado neste domínio.");
    return;
  }

  const script = document.currentScript;
  if (!(script instanceof HTMLScriptElement) || !script.src) return;

  const baseUrl = new URL(".", script.src);
  const widgetOrigin = baseUrl.origin;
  const widgetUrl = new URL("index.html", baseUrl);
  widgetUrl.searchParams.set("parentOrigin", window.location.origin);

  const frame = document.createElement("iframe");
  frame.id = FRAME_ID;
  frame.title = "Conte seu projeto para a Studio Tchê";
  frame.src = widgetUrl.toString();
  frame.setAttribute(
    "sandbox",
    "allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox",
  );
  frame.setAttribute("loading", "eager");
  frame.setAttribute("referrerpolicy", "strict-origin");
  frame.style.cssText = [
    "position:fixed",
    "right:24px",
    "bottom:max(24px,env(safe-area-inset-bottom))",
    "width:48px",
    "height:48px",
    "border:0",
    "border-radius:50%",
    "overflow:hidden",
    "background:transparent",
    "box-shadow:none",
    "z-index:2147483000",
    "color-scheme:normal",
  ].join(";");

  const OPEN_SHADOW = "0 16px 48px rgba(0,0,0,.14)";
  const mobileQuery = window.matchMedia("(max-width: 480px)");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  let isOpen = false;
  let contentHeight: number | null = null;
  let isReady = false;
  let modalRequested = false;
  let pendingOpen = false;
  let lastTrigger: HTMLElement | null = null;
  let previousOverflow = "";
  let scrollLocked = false;

  const backdrop = document.createElement("div");
  backdrop.setAttribute("aria-hidden", "true");
  backdrop.setAttribute("data-lenis-prevent", "");
  backdrop.style.cssText = [
    "position:fixed",
    "inset:0",
    "background:rgba(15,1,14,.5)",
    "opacity:0",
    "pointer-events:none",
    "transition:opacity 200ms ease",
    "z-index:2147482999",
    "overscroll-behavior:contain",
  ].join(";");

  const removeDraft = (): void => {
    try {
      window.localStorage.removeItem(CHAT_DRAFT_STORAGE_KEY);
    } catch {
      // O bloqueio do armazenamento não impede o funcionamento do chat.
    }
  };

  const readDraft = (): ChatDraft | null => {
    try {
      const stored = window.localStorage.getItem(CHAT_DRAFT_STORAGE_KEY);
      if (!stored) return null;
      const draft = parseChatDraft(JSON.parse(stored));
      if (draft) return draft;
      removeDraft();
    } catch {
      removeDraft();
    }
    return null;
  };

  const saveDraft = (value: unknown): void => {
    const draft = parseChatDraft(value);
    if (!draft) {
      removeDraft();
      return;
    }
    try {
      window.localStorage.setItem(CHAT_DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // O chat segue utilizável quando o navegador bloqueia armazenamento.
    }
  };

  // O iframe troca de tamanho na hora; a animação de abrir/fechar acontece dentro do widget.
  const setBackdrop = (visible: boolean): void => {
    if (visible) {
      if (!backdrop.isConnected) document.body.append(backdrop);
      backdrop.style.pointerEvents = "auto";
      requestAnimationFrame(() => {
        backdrop.style.opacity = "1";
      });
      return;
    }
    backdrop.style.pointerEvents = "none";
    backdrop.style.opacity = "0";
  };

  const lockScroll = (locked: boolean): void => {
    if (locked === scrollLocked) return;
    scrollLocked = locked;
    const root = document.documentElement;
    if (locked) {
      previousOverflow = root.style.overflow;
      root.style.overflow = "hidden";
    } else {
      root.style.overflow = previousOverflow;
    }
  };

  const applyCorner = (): void => {
    frame.style.left = "auto";
    frame.style.top = "auto";
    frame.style.transform = "none";
    frame.style.right = "24px";
    frame.style.bottom = "max(24px,env(safe-area-inset-bottom))";
  };

  const applySize = (wasOpen: boolean): void => {
    if (isOpen && mobileQuery.matches) {
      applyCorner();
      frame.style.transition = "none";
      frame.style.width = "100%";
      frame.style.height = "100dvh";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.borderRadius = "0";
      frame.style.boxShadow = "none";
      return;
    }
    if (isOpen && modalRequested) {
      const maxHeight = "calc(100dvh - 32px)";
      frame.style.right = "auto";
      frame.style.bottom = "auto";
      frame.style.left = "50%";
      frame.style.top = "50%";
      frame.style.transform = "translate(-50%,-50%)";
      frame.style.transition = wasOpen && !reducedMotionQuery.matches
        ? "height 420ms cubic-bezier(.22,1,.36,1)"
        : "none";
      frame.style.width = "392px";
      frame.style.height = contentHeight
        ? `min(${contentHeight}px, ${maxHeight})`
        : `min(640px, ${maxHeight})`;
      frame.style.borderRadius = "30px";
      frame.style.boxShadow = "0 24px 80px rgba(0,0,0,.28)";
      return;
    }
    applyCorner();
    if (!isOpen) {
      frame.style.transition = "none";
      frame.style.width = "48px";
      frame.style.height = "48px";
      frame.style.borderRadius = "50%";
      frame.style.boxShadow = "none";
      return;
    }
    const maxHeight = "calc(100dvh - max(24px, env(safe-area-inset-bottom)) - 16px)";
    frame.style.transition = wasOpen && !reducedMotionQuery.matches
      ? "height 420ms cubic-bezier(.22,1,.36,1),box-shadow 300ms ease"
      : "box-shadow 360ms ease 240ms";
    frame.style.width = "392px";
    frame.style.height = contentHeight
      ? `min(${contentHeight}px, ${maxHeight})`
      : `min(640px, ${maxHeight})`;
    frame.style.borderRadius = "30px";
    frame.style.boxShadow = OPEN_SHADOW;
  };

  window.addEventListener("message", (event: MessageEvent<unknown>) => {
    if (event.origin !== widgetOrigin || event.source !== frame.contentWindow) return;
    if (!event.data || typeof event.data !== "object") return;
    const message = event.data as {
      source?: string;
      type?: string;
      open?: boolean;
      height?: unknown;
      draft?: unknown;
    };
    if (message.source !== SOURCE) return;

    if (message.type === "ready") {
      frame.contentWindow?.postMessage(
        {
          source: SOURCE,
          type: "init",
          pageUrl: window.location.href,
          draft: readDraft(),
          mobile: mobileQuery.matches,
        },
        widgetOrigin,
      );
      isReady = true;
      if (pendingOpen) {
        pendingOpen = false;
        postOpen();
      }
      return;
    }

    if (message.type === "closing") {
      frame.style.transition = "box-shadow 200ms ease";
      frame.style.boxShadow = "none";
      if (modalRequested) setBackdrop(false);
      return;
    }

    if (message.type === "resize" && typeof message.open === "boolean") {
      const wasOpen = isOpen;
      isOpen = message.open;
      if (isOpen && (modalRequested || mobileQuery.matches)) lockScroll(true);
      if (isOpen && modalRequested && !mobileQuery.matches) {
        if (!wasOpen) lockScroll(true);
        setBackdrop(true);
      }
      if (!isOpen && wasOpen) {
        lockScroll(false);
        if (modalRequested) {
          setBackdrop(false);
          modalRequested = false;
          if (lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
          lastTrigger = null;
        }
      }
      contentHeight =
        typeof message.height === "number" &&
        Number.isFinite(message.height) &&
        message.height >= 200 &&
        message.height <= 640
          ? Math.ceil(message.height)
          : null;
      applySize(wasOpen);
      return;
    }

    if (message.type === "draft:save") saveDraft(message.draft);
    if (message.type === "draft:clear") removeDraft();
  });

  const postOpen = (): void => {
    frame.contentWindow?.postMessage(
      { source: SOURCE, type: "open", modal: true },
      widgetOrigin,
    );
  };

  const postClose = (): void => {
    frame.contentWindow?.postMessage({ source: SOURCE, type: "close" }, widgetOrigin);
  };

  const openChat = (trigger?: HTMLElement | null): void => {
    if (isOpen && !modalRequested) return;
    lastTrigger = trigger ?? (document.activeElement as HTMLElement | null);
    modalRequested = true;
    if (isOpen) {
      applySize(true);
      if (!mobileQuery.matches) {
        lockScroll(true);
        setBackdrop(true);
      }
      return;
    }
    if (isReady) postOpen();
    else pendingOpen = true;
  };

  const closeChat = (): void => {
    pendingOpen = false;
    if (isOpen) postClose();
  };

  backdrop.addEventListener("click", closeChat);
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isOpen && modalRequested) closeChat();
  });
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest<HTMLElement>("[data-chat-open]");
    if (!trigger) return;
    event.preventDefault();
    openChat(trigger);
  });

  (window as unknown as { StudioTcheChat: unknown }).StudioTcheChat = {
    open: () => openChat(),
    close: closeChat,
    isOpen: () => isOpen,
  };

  mobileQuery.addEventListener("change", () => {
    applySize(false);
    frame.contentWindow?.postMessage(
      {
        source: SOURCE,
        type: "viewport",
        mobile: mobileQuery.matches,
      },
      widgetOrigin,
    );
  });
  document.body.append(frame);
})();
